import { IConfettiOptions } from "./models"
import ParticleGenerator from "./particleGenerator"
import { confettiDefaults } from "./utils"

export class Confetti {
    constructor(canvas: HTMLCanvasElement, opts: Partial<IConfettiOptions>) {
        this.canvas = canvas
        const ctx = this.canvas.getContext('2d')
        if (!ctx) {
            throw new Error('Could not get canvas context')
        }
        this.context = ctx

        this.generator = new ParticleGenerator(this.canvas, () => (this.options as IConfettiOptions))
        this.options = opts
        this.update()
    }

    canvas: HTMLCanvasElement

    context: CanvasRenderingContext2D

    _options!: IConfettiOptions

    generator: ParticleGenerator

    rafId?: number

    get options(): Partial<IConfettiOptions> {
        return this._options
    }

    set options(opts: Partial<IConfettiOptions>) {
        const lastRunState = this._options && this._options.run
        const lastRecycleState = this._options && this._options.recycle
        this.setOptionsWithDefaults(opts)
        if (this.generator) {
            Object.assign(this.generator, this.options.confettiSource)
            if (typeof opts.recycle === 'boolean' && opts.recycle && lastRecycleState === false) {
                this.generator.lastNumberOfPieces = this.generator.particles.length
            }
        }
        if (typeof opts.run === 'boolean' && opts.run && lastRunState === false) {
            this.update()
        }
    }

    setOptionsWithDefaults = (opts: Partial<IConfettiOptions>) => {
        const computedConfettiDefaults = {
            confettiSource: {
                x: 0,
                y: 0,
                w: this.canvas.width,
                h: 0,
            },
        }
        this._options = { ...computedConfettiDefaults, ...confettiDefaults, ...opts }
        Object.assign(this, opts.confettiSource)
    }

    update = () => {
        const {
            options: {
                run,
                onConfettiComplete,
            },
            canvas,
            context,
        } = this
        if (run) {
            context.fillStyle = 'white'
            context.clearRect(0, 0, canvas.width, canvas.height)
        }
        if (this.generator.animate()) {
            this.rafId = requestAnimationFrame(this.update)
        } else {
            if (onConfettiComplete && typeof onConfettiComplete === 'function' && this.generator.particlesGenerated > 0) {
                onConfettiComplete.call(this, this)
            }
            this._options.run = false
        }
    }

    reset = () => {
        if (this.generator && this.generator.particlesGenerated > 0) {
            this.generator.particlesGenerated = 0
            this.generator.particles = []
            this.generator.lastNumberOfPieces = 0
        }
    }

    stop = () => {
        this.options = { run: false }
        if (this.rafId) {
            cancelAnimationFrame(this.rafId)
            this.rafId = undefined
        }
    }
}
